import React, { useEffect, useState, useMemo } from 'react';
import MainWrapper from '../wrappers/mainWrapper';
import CollapsedMatchBlock from '../restructured/CollapsedMatchBlock';
import LimitedExpandedMatchBlock from '../restructured/LimitedExpandedMatchBlock';
import { NavLink, useParams } from 'react-router-dom';
import '../../css/restructuredTransitions.css';
import PDFViewer from '../restructured/PDFViewer';
import LimitedFilters from '../restructured/LimitedFilters';
import LimitedZoneFilter from '../restructured/LimitedZoneFilter';
import GoIcon from '../../img/_Go.png';
import yieldIcon from '../../img/_Yield.png';
import PDFExport from '../restructured/assets/export-icon.svg';
import { Box, Typography, Button } from '@mui/material';
import LimitedNukeButton from '../restructured/LimitedNukeButton';
import revertMatch from '../restructured/functions/revertMatch';
import toggleExpanded from '../restructured/functions/toggleExpanded';
import {fetchData} from '../restructured/functions/fetchData';
import saveMatchResults from '../restructured/functions/saveMatchResults';
import saveMatchNotes from '../restructured/functions/saveMatchNotes';
import saveMarkForExport from '../restructured/functions/saveMarkForExport';
import {revertAllMatches} from '../restructured/functions/revertAllMatches';
import {saveLock} from '../restructured/functions/saveLock';

const LimitedResults = () => {
  const [data, setData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('loading-spinner');
  const { comparisonID } = useParams();
  const [visibleDocs, setVisibleDocs] = useState({ doc1: false, doc2: false });
  const [designationFilter, setDesignationFilter] = useState('All');
  const [zoneFilter, setZoneFilter] = useState([]); // Array of selected zone names

  const [isLoading, setIsLoading] = useState(false);
  const [hovered, setHovered] = useState(false);

  const toggleDoc = (docKey) => {
    setVisibleDocs((prev) => ({ ...prev, [docKey]: !prev[docKey] }));
  };

  const setCleanParsedEstData = (updated) => {
    setData((prev) => (prev ? { ...prev, cleanParsedEstData: updated } : prev));
  };

  const setCleanParsedInsData = (updated) => {
    setData((prev) => (prev ? { ...prev, cleanParsedInsData: updated } : prev));
  };

  const saveResults = async (resultsID, updatedResults) => {
    if (!data) return;
    const fullUpdatedData = { ...data, results: updatedResults };
    try {
      const response = await fetch(`${process.env.REACT_APP_UPDATE_RESULTS}/${resultsID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: fullUpdatedData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to save results');
    } catch (err) {
      console.error('❌ Error saving results:', err);
    }
  };

  const usedDoc2Ids = new Set();

  data?.results?.forEach(result => {
    const matchKey = Object.keys(result.Matches[0])[0];
    const doc2Ids = result.Matches[0][matchKey]["User Modified"]?.doc2?.ids || [];
    doc2Ids.forEach(id => usedDoc2Ids.add(id));
  });

  const doc1Zones = data?.cleanParsedEstData?.userModifiedZones
  ? Object.keys(data.cleanParsedEstData.userModifiedZones)
  : [];

  const unusedInsLineItems = data?.cleanParsedInsData?.lineItems?.filter(item => {
    const id = Object.keys(item)[0];
    return !usedDoc2Ids.has(id);
  });

  useEffect(() => {
    fetchData(comparisonID, setData, setLoadingStatus);
  
    const intervalId = setInterval(() => {
      fetchData(comparisonID, setData, setLoadingStatus);
    }, 4000);
  
    return () => clearInterval(intervalId);
  }, [comparisonID]);

  const getLineItemUsageMap = (results) => {
    const usageMap = {};
  
    results.forEach(result => {
      const match = result.Matches?.[0];
      const key = Object.keys(match)[0];
      const userModified = match[key]?.["User Modified"];
  
      if (!userModified) return;
  
      const doc1Ids = userModified.doc1?.ids || [];
      const doc2Ids = userModified.doc2?.ids || [];
  
      [...doc1Ids, ...doc2Ids].forEach(id => {
        usageMap[id] = (usageMap[id] || 0) + 1;
      });
    });
  
    return usageMap;
  };
  
  const lineItemUsageMap = useMemo(() => {
    if (!data?.results) return {};
    return getLineItemUsageMap(data.results);
  }, [data?.results, unusedInsLineItems]); 
  const [showModal, setShowModal] = useState(false);

  const handleClick = () => {
    setShowModal(true);
    setTimeout(() => setShowModal(false), 4000); // auto-close after 4s
  };


  return (
    <div className="outer-container">
      <MainWrapper />
      <div className="right-container">
        <div className="comparison-results-container">
          <LimitedFilters selectedFilter={designationFilter} onChange={setDesignationFilter} />
          <LimitedZoneFilter zones={doc1Zones} selectedZones={zoneFilter} onChange={setZoneFilter} />
          <>
          <button
            onClick={handleClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            disabled={isLoading}
            style={{
            position: 'absolute',
            top: '42px',
            right: '6px',
            zIndex: 1000,
            backgroundColor: isLoading ? '#5b5d5d' : '#49a7ab',
            color: '#ffffff',
            padding: '2px 0px 2px 4px',
            border: 'none',
            fontWeight: '600',
            fontSize: '14px',
            textTransform: 'uppercase',
            textDecoration: 'none',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            letterSpacing: '0.5px',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            transition: 'width 0.3s ease',
            width: hovered || isLoading ? '180px' : '32px',
            height: isLoading ? '36px' : ''
            }}
            id="preview-pdf-btn"
        >
            <img
            src={PDFExport}
            alt="Export PDF"
            style={{
                width: '24px',
                height: 'auto',
                objectFit: 'contain',
                flexShrink: 0,
            }}
            />
            <span
            style={{
                opacity: hovered || isLoading ? 1 : 0,
                transition: 'opacity 0.2s ease',
            }}
            >
            Preview PDF
            </span>
        </button>

        {showModal && (
            <Box
            sx={{
              position: 'fixed',
              top: '39%',
              left: '69%',
              transform: 'translate(-100%, -50%) scale(1)',
              bgcolor: '#fff',
              color: '#49a7ab',
              borderRadius: 2,
              boxShadow: 5,
              p: 1.5,
              zIndex: 9999,
              textAlign: 'center',
              minWidth: 500,
              whiteSpace: 'nowrap',
              animation: 'popout 0.2s ease',
            }}
          >
            <Typography
              variant="body1"
              sx={{ mb: 1, whiteSpace: 'nowrap', fontWeight: 500,  }}
            >
              Premium Functionality
            </Typography>
            <NavLink to="/accounts" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#49a7ab',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '.875rem',
                  '&:hover': { bgcolor: '#42999d' },
                }}
              >
                Subscribe
              </Button>
            </NavLink>
            <style>
              {`
                @keyframes popout {
                  0% { transform: translate(-100%, -50%) scale(0.7); opacity: 0; }
                  100% { transform: translate(-100%, -50%) scale(1); opacity: 1; }
                }
              `}
            </style>
          </Box>
        )}
        </>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>

          <LimitedNukeButton onClick={() => revertAllMatches(setData, comparisonID)} />

          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            {visibleDocs.doc1 && data?.files?.[0] && (
              <PDFViewer
                userId={data.claimData.user_id}
                filename={data.files[0]}
                onClose={() => toggleDoc('doc1')}
                docType="doc1"
              />
            )}
            {visibleDocs.doc2 && data?.files?.[1] && (
              <PDFViewer
                userId={data.claimData.user_id}
                filename={data.files[1]}
                onClose={() => toggleDoc('doc2')}
                docType="doc2"
              />
            )}
          </div>
          {data?.doc1_name && data?.doc2_name && (
            <div
              className="column-headers"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-end',
                marginBottom: '0px',
                padding: '0 16px', 
                position: 'absolute',
                width: '100%',
                boxSizing: 'border-box',
                top: (data?.estPriceList || data?.insPriceList) ? '60px' : '60px',
                borderBottom: '1px solid #42999d'
              }}
            >
              <div
                style={{
                  textAlign: 'left',
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px', // replaces &nbsp;&nbsp; for spacing
                  paddingRight: '0px',
                  borderRight: '1px solid #ccc',
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    color: '#42999d',
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    fontSize: '1.1rem',
                  }}
                >
                  {data.doc1_name}
                </h4>
                {data?.estPriceList && (
                  <i
                    style={{
                      margin: 0,
                      color: '#839299',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontStyle: 'normal', // make sure it's not italic unless you want italics
                    }}
                  >
                    Price List: {data?.estPriceList}
                    {data?.estPriceList !== data?.insPriceList ? (
                      <img
                        src={yieldIcon}
                        alt="yield icon"
                        style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                      />
                    ) : (
                      <img
                        src={GoIcon}
                        alt="go icon"
                        style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                      />
                    )}

                  </i>
                )}
              </div>

              <div
                style={{
                  textAlign: 'left',
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                  paddingLeft: '12px',
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    color: '#42999d',
                    whiteSpace: 'nowrap',
                    fontWeight: '600',
                    fontSize: '1.1rem',
                  }}
                >
                  {data.doc2_name}
                </h4>
                {data?.insPriceList && (
                  <i
                    style={{
                      margin: 0,
                      color: '#839299',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontStyle: 'normal',
                    }}
                  >
                    Price List: {data?.insPriceList}
                    {data?.estPriceList === data?.insPriceList && (
                      <img
                        src={GoIcon}
                        alt="go icon"
                        style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                      />
                    )}
                  </i>
                )}
              </div>

            </div>
          )}

            <div id="pairings" className="pairings-container">
              <div className={loadingStatus}></div>
              {data?.results
                ?.filter((result) => {
                    const matchKey = Object.keys(result.Matches[0])[0];
                    const match = result.Matches[0][matchKey];

                    // Filter by designation
                    const designation = match?.['User Modified']?.Designation || 'No-Match';
                    if (designationFilter !== 'All' && designation !== designationFilter) {
                    return false;
                    }

                    // Filter by zone (multi-select: zoneFilter is an array)
                    if (zoneFilter && zoneFilter.length > 0) {
                    const doc1Ids = match?.['User Modified']?.doc1?.ids || [];
                    if (doc1Ids.length === 0) return false;

                    // Get zones for all doc1 line item ids
                    const zonesForIds = doc1Ids.map((id) => {
                        const lineItemObj = data.cleanParsedEstData.lineItems.find(
                        (li) => Object.keys(li)[0] === id
                        );
                        if (!lineItemObj) return null;

                        const lineItemData = lineItemObj[id];
                        return lineItemData.userModifiedZone || null;
                    }).filter(Boolean);

                    // Check if ANY of the zones match any selected zoneFilter
                    const matchedZones = zonesForIds.filter(z => zoneFilter.includes(z));
                    if (matchedZones.length === 0) {
                        return false;
                    }
                    }

                    return true;
                })
                .slice(0, 10)   // ✅ LIMIT TO FIRST 10
                .map((result) => {
                    const matchKey = Object.keys(result.Matches[0])[0];
                    const match = result.Matches[0][matchKey];
                    const doc1ID = match?.['User Modified']?.IDs?.Doc1_ID;
                    const isExpanded = match?.expanded;

                    return (
                    <div key={doc1ID} id={match["System Detected"].doc1.ids[0]} className="match-collapse">
                        {isExpanded ? (
                        <LimitedExpandedMatchBlock
                            match={match}
                            cleanParsedEstData={data.cleanParsedEstData}
                            setCleanParsedEstData={setCleanParsedEstData}
                            cleanParsedInsData={data.cleanParsedInsData}
                            setCleanParsedInsData={setCleanParsedInsData}
                            headersOne={data.headersOne}
                            headersTwo={data.headersTwo}
                            docname1={data.doc1_name}
                            docname2={data.doc2_name}
                            claimId={data.compID}
                            visibleDocs={visibleDocs}
                            toggleDoc={toggleDoc}
                            onToggle={() => toggleExpanded(result.id, setData, saveResults)}
                            onSaveMatchUpdate={(updatedSystem) => saveMatchResults(result.id, updatedSystem, setData, saveResults)}
                            onSaveMatchNotes={(newNotes) => saveMatchNotes(result.id, newNotes, setData, saveResults)}
                            onSaveMarkForExport={(val) => saveMarkForExport(result.id, val, setData, saveResults)}
                            onSaveLockClick={(lineItemId) => saveLock(result.id, lineItemId, setData, comparisonID)}
                            onRevertMatch={() => revertMatch(result.id, setData, comparisonID)}
                            resultsID={data.resultsID}
                            lineItemUsageMap={lineItemUsageMap}
                        />
                        ) : (
                        <CollapsedMatchBlock
                            match={match}
                            cleanParsedEstData={data.cleanParsedEstData}
                            setCleanParsedEstData={setCleanParsedEstData}
                            cleanParsedInsData={data.cleanParsedInsData}
                            setCleanParsedInsData={setCleanParsedInsData}
                            headersOne={data.headersOne}
                            headersTwo={data.headersTwo}
                            docname1={data.doc1_name}
                            docname2={data.doc2_name}
                            claimId={data.compID}
                            userId={data.claimData.user_id}
                            filename1={data.files[0]}
                            filename2={data.files[1]}
                            visibleDocs={visibleDocs}
                            toggleDoc={toggleDoc}
                            onToggle={() => toggleExpanded(result.id, setData, saveResults)}
                            lineItemUsageMap={lineItemUsageMap}
                            doc2NoMatch={false}
                        />
                        )}
                    </div>
                    );
                })}

                <h4 style={{fontSize: '36px', padding: '16px 0', boxSizing: 'border-box', color: '#49A7AB', width: '100%', display: 'flex', justifyContent: 'center'}}>
                    Want to see more? click
                    &nbsp;
                    <NavLink style={{color: '#42999D'}} to="/accounts">
                        here
                    </NavLink>
                </h4>
            </div>


        </div>
      </div>
    </div>
  );
};

export default LimitedResults;
